import { getGeeTestCaptcha } from '@/lib/api/geetestApi'
import { GeetestKeyEnum } from '@/typings/domain'
import { GeetestCaptchaResult, startCaptcha } from '@modules/geetest-module'
import { useCallback, useEffect, useRef } from 'react'
import { Platform } from 'react-native'

export type GeetestResultData = Pick<GeetestCaptchaResult, 'pass_token' | 'lot_number' | 'gen_time' | 'captcha_output'> & {
  type: GeetestKeyEnum
}

export type GeetestType = 'sms'

const geetestTypeMap = Platform.select<Record<GeetestType, GeetestKeyEnum>>({
  ios: { sms: GeetestKeyEnum.SmsIos },
  android: { sms: GeetestKeyEnum.SmsAndroid },
  web: { sms: GeetestKeyEnum.SendSmsV4 },
})

type GeeTestWrapperProps = {
  type: GeetestType
  onSuccess?: (result: GeetestResultData) => void
  onError?: (error: string | undefined) => void
  children: (trigger: () => Promise<GeetestResultData>) => React.ReactNode
}

const CANCELLED_ERRORS = ['Captcha verification did not complete', 'cancelled']

export default function GeeTest({ type, onSuccess, onError, children }: GeeTestWrapperProps) {
  const callbackRef = useRef({ onSuccess, onError })
  const promiseRef = useRef<
    { resolve: (result: GeetestResultData) => void; reject: (error: Error) => void } | undefined
  >(undefined)
  callbackRef.current = { onSuccess, onError }
  const hasPending = useRef(false)
  const captchaIdRef = useRef<{ type: GeetestKeyEnum; id: string } | null>(null)

  const showCaptcha = useCallback(async () => {
    if (!captchaIdRef.current) throw new Error('Captcha ID is not set')

    const result = await startCaptcha(captchaIdRef.current.id)
    if (!result.success) {
      callbackRef.current.onError?.(result.error)
      if (!CANCELLED_ERRORS.includes(result.error ?? '')) {
        promiseRef.current?.reject(new Error(result.error))
      }
      promiseRef.current = undefined
      return
    }
    const geetest4Data: GeetestResultData = {
      captcha_output: result.result.captcha_output,
      gen_time: result.result.gen_time,
      lot_number: result.result.lot_number,
      pass_token: result.result.pass_token,
      type: captchaIdRef.current.type,
    }

    callbackRef.current.onSuccess?.(geetest4Data)
    promiseRef.current?.resolve(geetest4Data)
    promiseRef.current = undefined
  }, [])

  const initCaptcha = useCallback(async () => {
    captchaIdRef.current = null

    const geetestType = geetestTypeMap?.[type]
    if (!geetestType) throw new Error(`Invalid geetest type: ${type}`)

    const res = await getGeeTestCaptcha({ type: geetestType })
    const captchaId = res.data?.initCaptcha?.geetestId
    if (captchaId) {
      captchaIdRef.current = { id: captchaId, type: geetestType }
      if (hasPending.current) {
        hasPending.current = false
        showCaptcha()
      }
    }
  }, [type, showCaptcha])

  useEffect(() => {
    initCaptcha()
  }, [initCaptcha])

  const trigger = useCallback(() => {
    if (captchaIdRef.current) showCaptcha()
    else hasPending.current = true

    return new Promise<GeetestResultData>((resolve, reject) => {
      promiseRef.current = { resolve, reject }
    })
  }, [showCaptcha])

  return children(trigger)
}
